import { AlertCircle, Clock, Lock, Mail, Pencil, Play, Workflow } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

import type { Flow } from '@grafx-vdp/shared';
import { resolveDeliveryStrategy, resolvePickupStrategy } from '@grafx-vdp/shared';

import { useFlowsQuery } from '@/api/hooks.js';
import { EditFlowDialog } from '@/components/EditFlowDialog.js';
import { EmptyState } from '@/components/EmptyState.js';
import { TriggerFlowDialog } from '@/components/TriggerFlowDialog.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.js';
import { Skeleton } from '@/components/ui/skeleton.js';

export function FlowsPage() {
  const flowsQuery = useFlowsQuery();
  const [triggerFlow, setTriggerFlow] = useState<Flow | null>(null);
  const [editFlow, setEditFlow] = useState<Flow | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Flows</h1>
        <p className="text-sm text-muted-foreground">
          Flow configuration. SFTP connection details are in Settings.
        </p>
      </div>

      {flowsQuery.isError ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-6 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            Failed to load flows.
          </CardContent>
        </Card>
      ) : null}

      {flowsQuery.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      ) : null}

      {!flowsQuery.isLoading && !flowsQuery.isError && (flowsQuery.data ?? []).length === 0 ? (
        <Card>
          <EmptyState
            icon={Workflow}
            title="No flows yet"
            description="This tenant has no configured flows."
          />
        </Card>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        {(flowsQuery.data ?? []).map((flow) => (
          <FlowCard
            key={flow.id}
            flow={flow}
            onTrigger={() => setTriggerFlow(flow)}
            onEdit={() => setEditFlow(flow)}
          />
        ))}
      </div>

      <TriggerFlowDialog
        flow={triggerFlow}
        open={!!triggerFlow}
        onOpenChange={(open) => {
          if (!open) setTriggerFlow(null);
        }}
      />

      <EditFlowDialog
        flow={editFlow}
        open={!!editFlow}
        onOpenChange={(open) => {
          if (!open) setEditFlow(null);
        }}
      />
    </div>
  );
}

function FlowCard({
  flow,
  onTrigger,
  onEdit,
}: {
  flow: Flow;
  onTrigger: () => void;
  onEdit: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="flex items-baseline gap-2">
            <span>{flow.name}</span>
            {flow.active ? (
              <Badge
                variant="outline"
                className="border-transparent bg-[var(--palette-success-light)] text-[var(--palette-success-dark)]"
              >
                active
              </Badge>
            ) : (
              <Badge variant="secondary">inactive</Badge>
            )}
          </CardTitle>
          <Button
            size="sm"
            onClick={onTrigger}
            disabled={!flow.active}
            title={flow.active ? 'Trigger this flow' : 'Flow is inactive'}
          >
            <Play className="mr-1 h-3.5 w-3.5" />
            Trigger now
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">{flow.description}</p>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <Row icon={<Clock className="h-3.5 w-3.5" />} label="Schedule">
          {flow.cron ? <code className="font-mono">{flow.cron}</code> : '—'}{' '}
          <span className="text-xs text-muted-foreground">({flow.triggerType})</span>
        </Row>
        <Row label="Template">
          <code className="font-mono text-xs">{flow.templateId}</code>
        </Row>
        <Row label="Output settings">
          <code className="font-mono text-xs">{flow.outputSettingsId}</code>
        </Row>
        <Row label="Output engine">
          <span className="text-xs uppercase">{flow.outputEngine}</span>
        </Row>
        {flow.inputConfig.engine === 'sftp' ? (
          <>
            <Row label="Pickup folder">
              <code className="font-mono text-xs">{flow.inputConfig.path}</code>
            </Row>
            <Row label="After pickup">
              <span className="text-xs">{pickupStrategyLabel(flow.inputConfig)}</span>
            </Row>
          </>
        ) : null}
        {flow.outputConfig.engine === 'sftp' ? (
          <>
            <Row label="Delivery folder">
              <code className="font-mono text-xs">{flow.outputConfig.path}</code>
            </Row>
            <Row label="Delivery naming">
              <span className="text-xs">{deliveryStrategyLabel(flow.outputConfig)}</span>
            </Row>
            <Row label="Output filename">
              <code className="break-all font-mono text-xs">
                {flow.filenameConvention || '(default source-suffix pattern)'}
              </code>
            </Row>
          </>
        ) : null}
        <Row icon={<Mail className="h-3.5 w-3.5" />} label="Notifications">
          {flow.notificationEmails.length === 0 ? (
            <span className="text-muted-foreground">no recipients</span>
          ) : (
            <span className="text-xs">{flow.notificationEmails.join(', ')}</span>
          )}
        </Row>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="mr-1 h-3.5 w-3.5" />
            Edit
          </Button>
          <Button variant="outline" size="sm" disabled className="opacity-60">
            <Lock className="mr-1 h-3.5 w-3.5" />
            Delete
            <Badge variant="outline" className="ml-2 text-[10px]">
              Phase 1
            </Badge>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function pickupStrategyLabel(config: Extract<Flow['inputConfig'], { engine: 'sftp' }>): string {
  const strategy = resolvePickupStrategy(config);
  if (strategy === 'none') return 'No action';
  if (strategy === 'delete') return 'Delete picked-up file';
  return config.pickedUpPath
    ? `Move to ${config.pickedUpPath}`
    : 'Move picked-up file (path not set)';
}

function deliveryStrategyLabel(config: Extract<Flow['outputConfig'], { engine: 'sftp' }>): string {
  const strategy = resolveDeliveryStrategy(config);
  return strategy === 'source-suffix' ? 'Reuse pickup filename + suffix' : 'Custom pattern';
}

function Row({
  icon,
  label,
  children,
}: {
  icon?: ReactNode;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5 w-32 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          {icon}
          {label}
        </span>
      </div>
      <div className="min-w-0 flex-1 break-words">{children}</div>
    </div>
  );
}
